import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiAlertCircle,
  FiCalendar,
  FiLock,
  FiMail,
  FiShield,
  FiUser,
} from 'react-icons/fi';

import AuthLayout from '../components/auth/AuthLayout';
import TextField from '../components/auth/TextField';
import PasswordStrength from '../components/auth/PasswordStrength';
import { useAuth } from '../context/AuthContext';

const MIN_PASSWORD_LENGTH = 8;

const EMPTY = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  acceptedTerms: false,
};

const ASIDE_POINTS = [
  {
    Icon: FiShield,
    title: 'Every therapist is verified',
    text: 'Registration, insurance and qualifications checked before anyone is listed.',
  },
  {
    Icon: FiCalendar,
    title: 'Book around your life',
    text: 'See real availability, including evenings and weekends, and book in a couple of taps.',
  },
  {
    Icon: FiUser,
    title: 'Switch whenever you need',
    text: 'Fit matters more than credentials. Rebook with someone new any time, no explanation needed.',
  },
];

/**
 * Mirrors the server-side rules in `backend/auth.py`. Client validation is for
 * fast feedback only — the server revalidates everything, and its field errors
 * overwrite these on submit.
 */
function validate(values) {
  const errors = {};

  if (!values.name.trim()) {
    errors.name = 'Please enter your full name.';
  } else if (values.name.trim().length < 2) {
    errors.name = 'That name looks too short.';
  }

  if (!values.email.trim()) {
    errors.email = 'Please enter your email address.';
  } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(values.email.trim())) {
    errors.email = 'That doesn’t look like a valid email address.';
  }

  if (!values.password) {
    errors.password = 'Please choose a password.';
  } else if (values.password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }

  if (!values.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password.';
  } else if (values.confirmPassword !== values.password) {
    errors.confirmPassword = 'Both passwords need to match.';
  }

  if (!values.acceptedTerms) {
    errors.acceptedTerms = 'Please accept the terms to continue.';
  }

  return errors;
}

export default function SignupPage() {
  const { signup, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Where to land after signing up: back where they came from, or home.
  const redirectTo = location.state?.from ?? '/';

  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, navigate, redirectTo]);

  const handleChange = (event) => {
    const { name, type, value, checked } = event.target;
    const next = type === 'checkbox' ? checked : value;

    setValues((prev) => ({ ...prev, [name]: next }));
    // Clear the field's error the moment they start correcting it.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    setFormError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    const clientErrors = validate(values);
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }

    setSubmitting(true);
    try {
      await signup({
        name: values.name.trim(),
        email: values.email.trim(),
        password: values.password,
        confirmPassword: values.confirmPassword,
        acceptedTerms: values.acceptedTerms,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err.body?.errors) {
        setErrors(err.body.errors);
      } else {
        setFormError(
          err.status
            ? err.message
            : 'Couldn’t reach the server. Check your connection and try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      aside={
        <>
          <h2 className="mm-auth__aside-title">
            Therapy is hard enough. Finding a therapist shouldn’t be.
          </h2>
          <p className="mm-auth__aside-text">
            Create a free account to save therapists, track your enquiries and
            book sessions in one place.
          </p>

          <ul className="mm-auth__points">
            {ASIDE_POINTS.map(({ Icon, title, text }) => (
              <li className="mm-auth__point" key={title}>
                <Icon size={19} aria-hidden="true" />
                <span>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </span>
              </li>
            ))}
          </ul>

          <p className="mm-auth__aside-note">
            Creating an account is free. We never sell your data, and nothing you
            save here is shared with a therapist until you book.
          </p>
        </>
      }
    >
      <h1 className="mm-auth__title">Create your account</h1>
      <p className="mm-auth__subtitle">
        Already have one? <Link to="/login">Log in instead</Link>.
      </p>

      <form className="mm-auth__form" onSubmit={handleSubmit} noValidate>
        {formError && (
          <div className="mm-form-alert mm-form-alert--error" role="alert">
            <FiAlertCircle size={16} aria-hidden="true" />
            <span>{formError}</span>
          </div>
        )}

        <TextField
          label="Full name"
          name="name"
          icon={FiUser}
          placeholder="Priya Sharma"
          autoComplete="name"
          required
          value={values.name}
          onChange={handleChange}
          error={errors.name}
        />

        <TextField
          label="Email address"
          name="email"
          type="email"
          icon={FiMail}
          placeholder="you@example.com"
          autoComplete="email"
          required
          value={values.email}
          onChange={handleChange}
          error={errors.email}
        />

        <TextField
          label="Password"
          name="password"
          type="password"
          icon={FiLock}
          placeholder="At least 8 characters"
          autoComplete="new-password"
          required
          value={values.password}
          onChange={handleChange}
          error={errors.password}
        >
          <PasswordStrength password={values.password} />
        </TextField>

        <TextField
          label="Confirm password"
          name="confirmPassword"
          type="password"
          icon={FiLock}
          placeholder="Type it again"
          autoComplete="new-password"
          required
          value={values.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
        />

        <div className="mb-3">
          <label className="mm-check">
            <input
              type="checkbox"
              name="acceptedTerms"
              checked={values.acceptedTerms}
              onChange={handleChange}
              aria-invalid={Boolean(errors.acceptedTerms)}
            />
            <span>
              I agree to the <a href="#terms">Terms of Use</a> and{' '}
              <a href="#privacy">Privacy Policy</a>.
            </span>
          </label>
          {errors.acceptedTerms && (
            <span className="mm-field__error" role="alert">
              <FiAlertCircle size={13} aria-hidden="true" />
              {errors.acceptedTerms}
            </span>
          )}
        </div>

        <Button type="submit" className="mm-auth__submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />
              Creating account…
            </>
          ) : (
            'Create account'
          )}
        </Button>
      </form>

      <div className="mm-auth__footer">
        Are you a therapist? <Link to="/apply">Apply to be listed</Link>
      </div>
    </AuthLayout>
  );
}
